import type {
  CreateInboxV2ReplyInput,
  InboxV2BoardPayload,
  InboxV2Conversation,
  InboxV2ConversationMessage,
  InboxV2ConversationStatus,
  InboxV2SystemConversationMessage,
  UpdateInboxV2MessageInput,
} from "../../../types";
import {
  isInboxV2CommentMessage,
  isInboxV2PostSentimentMessage,
} from "../../../ui-meta/message-meta";
import { getInboxV2AccountDisplayName } from "../../../utils/account-display";
import { formatInboxV2Timestamp } from "../../../utils/date-format";

export function patchMessageRecord(
  message: InboxV2ConversationMessage,
  input: UpdateInboxV2MessageInput
): InboxV2ConversationMessage {
  if (!isInboxV2CommentMessage(message)) {
    return isInboxV2PostSentimentMessage(message) && input.sentiment
      ? { ...message, sentiment: input.sentiment }
      : message;
  }

  return {
    ...message,
    body: input.body ?? message.body,
    directMessageId:
      input.directMessageId === undefined
        ? message.directMessageId
        : (input.directMessageId ?? undefined),
    isDeleted: input.isDeleted ?? message.isDeleted,
    isHidden: input.isHidden ?? message.isHidden,
    sentiment: input.sentiment ?? message.sentiment,
  };
}

export function createOptimisticReplyMessage(
  payload: InboxV2BoardPayload | undefined,
  input: CreateInboxV2ReplyInput,
  optimisticMessageId = `optimistic-message-${Date.now()}`
): InboxV2ConversationMessage | null {
  const conversation = payload?.conversations.find(
    (item) => item.id === input.conversationId
  );

  if (!conversation) {
    return null;
  }

  const account = payload?.accounts.find(
    (item) => item.id === conversation.accountId
  );
  const now = new Date().toISOString();
  const targetLayout = getOptimisticReplyTargetLayout(conversation);
  const subKind: "internal" | "social" = input.isInternal
    ? "internal"
    : "social";

  const baseMessage = {
    id: optimisticMessageId,
    kind: "message-creating" as const,
    sourceType: "message-creating",
    subKind,
    targetLayout,
    conversationId: input.conversationId,
    authorName: input.isInternal
      ? "Internal comment"
      : getInboxV2AccountDisplayName(account),
    authorHandle: input.isInternal
      ? "internal"
      : (account?.login ?? account?.id ?? "account"),
    authorPlatform: account?.platform,
    avatarUrl: input.isInternal ? undefined : account?.avatarUrl,
    body: input.text.trim(),
    attachments: input.optimisticAttachments,
    createdAt: now,
    createdAtLabel: formatInboxV2Timestamp(now),
    isBrand: true,
    isConversationAccountSender: !input.isInternal,
    retryInput: input,
    sendStatus: "sending" as const,
  };

  return {
    ...baseMessage,
    directReplyToMessageId: input.replyToId,
    rollbackConversationIndicators: getRollbackConversationIndicators(
      conversation,
      input
    ),
    rollbackConversationStatus: getRollbackConversationStatus(
      conversation,
      input
    ),
  };
}

export function getOptimisticReplyConversationStatus(
  conversation: InboxV2Conversation,
  input: CreateInboxV2ReplyInput
): InboxV2ConversationStatus {
  if (input.isInternal) {
    return conversation.status;
  }

  if (input.closeConversation) {
    return "closed";
  }

  if (conversation.status === "closed") {
    return "open";
  }

  return conversation.status;
}

export function getOptimisticReplyConversationIndicators(
  conversation: InboxV2Conversation,
  input: CreateInboxV2ReplyInput
): InboxV2Conversation["indicators"] {
  const mentionedUserIds = new Set(
    Array.from(input.text.matchAll(/@\[([a-f\d]{24})]/gm), (match) => match[1])
  );
  const mentionsCurrentUser = input.currentUserMentionIds?.some((userId) =>
    mentionedUserIds.has(userId)
  );

  if (
    !input.isInternal ||
    !mentionsCurrentUser ||
    conversation.indicators.includes("mention")
  ) {
    return conversation.indicators;
  }

  return [...conversation.indicators, "mention"];
}

function getRollbackConversationIndicators(
  conversation: InboxV2Conversation,
  input: CreateInboxV2ReplyInput
) {
  const optimisticIndicators = getOptimisticReplyConversationIndicators(
    conversation,
    input
  );

  return optimisticIndicators === conversation.indicators
    ? undefined
    : conversation.indicators;
}

function getRollbackConversationStatus(
  conversation: InboxV2Conversation,
  input: CreateInboxV2ReplyInput
) {
  const optimisticStatus = getOptimisticReplyConversationStatus(
    conversation,
    input
  );

  return optimisticStatus === conversation.status
    ? undefined
    : conversation.status;
}

function getOptimisticReplyTargetLayout(
  conversation: InboxV2Conversation
): "comment" | "direct-message" | "review" {
  if (conversation.messageType === "dm") {
    return "direct-message";
  }

  if (
    conversation.platform === "google-business" &&
    conversation.messageType === "review"
  ) {
    return "review";
  }

  return "comment";
}

export function addMessageToConversationRecord(
  conversation: InboxV2Conversation,
  message: InboxV2ConversationMessage
): InboxV2Conversation {
  const messages = [...conversation.messages, message].toSorted((left, right) =>
    left.createdAt.localeCompare(right.createdAt)
  );

  return {
    ...conversation,
    lastActivityLabel: message.createdAtLabel,
    lastMessageAt: message.createdAt,
    messages,
    updatedAt: message.createdAt,
  };
}

export function createInboxV2ConfirmedSystemMessage(input: {
  actorName: string;
  assigneeName?: string;
  createdAt?: string;
  status?: InboxV2ConversationStatus;
  type: "assign" | "status";
}): InboxV2SystemConversationMessage {
  const createdAt = input.createdAt ?? new Date().toISOString();
  const actorName = input.actorName.trim() || "User";

  return {
    accountId: undefined,
    authorHandle: "system",
    authorName: actorName,
    body:
      input.type === "assign"
        ? `${actorName} assigned this conversation to ${input.assigneeName?.trim() || "Unknown"} at `
        : `${actorName} ${input.status === "closed" ? "closed" : "reopened"} this conversation at `,
    createdAt,
    createdAtLabel: formatInboxV2Timestamp(createdAt),
    id: `system-${input.type}-${createdAt}-${Math.random().toString(36).slice(2, 8)}`,
    isSystem: true,
    kind: "system",
    sourceType: "system",
  };
}

export function removeMessageFromConversationRecord(
  conversation: InboxV2Conversation,
  messageId: string
): InboxV2Conversation {
  return {
    ...conversation,
    messages: conversation.messages.filter(
      (message) => message.id !== messageId
    ),
  };
}

export function upsertMessageInConversationRecord(
  conversation: InboxV2Conversation,
  message: InboxV2ConversationMessage
): InboxV2Conversation {
  const hasExistingMessage = conversation.messages.some(
    (item) => item.id === message.id
  );

  if (!hasExistingMessage) {
    return addMessageToConversationRecord(conversation, message);
  }

  const messages = conversation.messages
    .map((item) => (item.id === message.id ? message : item))
    .toSorted((left, right) => left.createdAt.localeCompare(right.createdAt));

  return {
    ...conversation,
    lastActivityLabel: message.createdAtLabel,
    lastMessageAt: message.createdAt,
    messages,
    updatedAt: message.createdAt,
  };
}

export function replaceMessageInConversationRecord(
  conversation: InboxV2Conversation,
  previousMessageId: string,
  nextMessage: InboxV2ConversationMessage
): InboxV2Conversation {
  if (
    !conversation.messages.some((message) => message.id === previousMessageId)
  ) {
    return upsertMessageInConversationRecord(conversation, nextMessage);
  }

  const messages = conversation.messages
    .filter(
      (message) =>
        message.id !== nextMessage.id || message.id === previousMessageId
    )
    .map((message) =>
      message.id === previousMessageId ? nextMessage : message
    )
    .toSorted((left, right) => left.createdAt.localeCompare(right.createdAt));

  return {
    ...conversation,
    lastActivityLabel: nextMessage.createdAtLabel,
    lastMessageAt: nextMessage.createdAt,
    messages,
    updatedAt: nextMessage.createdAt,
  };
}
